// Client rarity helpers, costs come straight from the shared protocol tables so
// the UI can never disagree with the server, plus the visual vocabulary (accent
// colour + foil/glow class) used by card tiles and the pack-opening reveal.

import {
  CRAFT_COST, DISMANTLE_YIELD, RARITY_LABEL, FOIL_INFO,
  foilDismantleYield as _foilDismantleYield,
  type Rarity, type FoilGrade,
} from "@ygoevo/world-protocol";

export type { Rarity, FoilGrade };
export { CRAFT_COST, DISMANTLE_YIELD, RARITY_LABEL, FOIL_INFO };

export const craftCost = (r?: Rarity): number => CRAFT_COST[r ?? "N"];
export const dismantleYield = (r?: Rarity): number => DISMANTLE_YIELD[r ?? "N"];
export const rarityLabel = (r?: Rarity): string => RARITY_LABEL[r ?? "N"];
/** Shards from dismantling one foil copy, tolerates an unknown rarity (→ Common). */
export const foilDismantleYield = (r: Rarity | undefined, grade: number): number =>
  _foilDismantleYield(r ?? "N", grade);

/** Accent colour per tier (gold UR, violet SR, blue R, slate N). */
export const RARITY_COLOR: Record<Rarity, string> = {
  N: "#94a3b8",
  R: "#5aa6ff",
  SR: "#c9a6ff",
  UR: "#ffce54",
};
export const rarityColor = (r?: Rarity): string => RARITY_COLOR[r ?? "N"];

/** Short rank glyph for badges. */
export const RARITY_TAG: Record<Rarity, string> = { N: "N", R: "R", SR: "SR", UR: "UR" };

/** Is this a "shiny" tier that gets the holographic foil treatment? */
export const isShiny = (r?: Rarity): boolean => r === "SR" || r === "UR";

/** Rarity-ring CSS class (defined in app.css) applied to a card frame by rarity. */
export const foilClass = (r?: Rarity): string =>
  r === "UR" ? "foil-ur" : r === "SR" ? "foil-sr" : r === "R" ? "foil-r" : "";

/** Animated foil-finish CSS class by grade (1 Holo → 2 Prismatic → 3 Galaxy);
 *  "" for a plain (grade 0/undefined) copy. The rarer the grade, the flashier. */
export const foilGradeClass = (grade?: number): string =>
  grade === 3 ? "foil-g3" : grade === 2 ? "foil-g2" : grade === 1 ? "foil-g1" : "";

/** Human label for a foil grade (e.g. "Prismatic Foil"); "" when plain. */
export const foilLabel = (grade?: number): string =>
  grade && grade in FOIL_INFO ? FOIL_INFO[grade as FoilGrade].label : "";

/** Accent colour for a foil grade (callouts / auras); falls back to white. */
export const foilColor = (grade?: number): string =>
  grade && grade in FOIL_INFO ? FOIL_INFO[grade as FoilGrade].color : "#ffffff";
